import React from 'react';
import { Select, select, t, Trans } from '@lingui/macro';

import FrequentlyAskedQuestions from '@rover/components/src/FrequentlyAskedQuestions';
import { Link, Paragraph } from '@rover/kibble/core';

import { ServiceFaq } from './constants';
import FirstFAQIsOpen from './FirstFAQIsOpen';
import FrequentlyAskedQuestion from './FrequentlyAskedQuestion';
import UnorderedList, { ListItem } from './UnorderedList';
import useFacts from './useFacts';

const DogBoardingFaqs: React.FC<ServiceFaq> = ({
  localizedLinkUrls: links,
  facts,
  locationName,
  localityType,
  htmlRef,
  isFacilitiesEnabled = false,
}) => {
  // we create these two variables so translators can distinguish between them in the lingui selects
  const cityName = locationName;
  const neighborhoodName = locationName;

  const {
    avgRepeatCustomerAmongSittersWithRepeatNum,
    maxSearchableSittersNum,
    medianListPricePerUnitLocalCurrencyNum,
    sittersCanProvideDailyExercisePct,
    sittersCanProvideMedicationPct,
    sittersCanProvideSpecialCarePct,
    sittersWithMinResponseTimeIn60minPct,
    yearsOfExperienceNum,
    p10GrandTotalNum,
    p90GrandTotalNum,
  } = useFacts(facts);
  return (
    <FrequentlyAskedQuestions
      htmlRef={htmlRef}
      data-testid="dog-boarding-faqs"
      mt={['6x', '16x']}
      pb={['4x', '8x']}
    >
      <FrequentlyAskedQuestions.Header webProps={{ responsive: false }}>
        <Trans>Dog Boarding FAQs</Trans>
      </FrequentlyAskedQuestions.Header>
      <FrequentlyAskedQuestions.Subheader>
        <Trans>Frequently asked questions about Rover</Trans>
      </FrequentlyAskedQuestions.Subheader>
      <FirstFAQIsOpen>
        {/* 1 */}
        {isFacilitiesEnabled && (
          <FrequentlyAskedQuestion question="What’s the difference between in-home dog boarding and facility-based dog boarding?">
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              Only Rover provides comprehensive {locationName} dog boarding options that meet your
              dog's unique needs. Anxious or special needs dogs who prefer a 1:1 environment are a
              good fit for in-home dog boarding, while energetic pups who benefit from socialization
              and activity are a good fit for dog boarding facilities.
            </Paragraph>
          </FrequentlyAskedQuestion>
        )}
        {/* 2 */}
        {medianListPricePerUnitLocalCurrencyNum &&
          p10GrandTotalNum &&
          p90GrandTotalNum &&
          'whatAreTheServiceFees' in links &&
          (() => {
            const {
              yearMonthLastUpdated,
              asCurrency: formattedAmount,
              asWeekCurrency: weekFormattedAmount,
            } = medianListPricePerUnitLocalCurrencyNum;
            const { asCurrency: minimumFormattedAmount } = p10GrandTotalNum;
            const { asCurrency: maximumFormattedAmount } = p90GrandTotalNum;

            return (
              <FrequentlyAskedQuestion
                question={select(localityType, {
                  city: `How much does it cost to hire a sitter for dog boarding in ${cityName}?`,
                  neighborhood: `How much does it cost to hire a sitter for dog boarding in ${neighborhoodName}?`,
                  other: `How much does it cost to hire a ${locationName} sitter for dog boarding?`,
                })}
              >
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        As of {yearMonthLastUpdated}, the median cost of dog boarding in {cityName}{' '}
                        is {formattedAmount} per night, including{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          service fees
                        </Link>
                        . Boarding your dog for one week typically costs {weekFormattedAmount}.
                        Sitters on Rover set their own rates based on their qualifications, your
                        dog's needs, and their space's amenities. As a result, most {cityName} dog
                        boarding rates can be found as low as {minimumFormattedAmount} to as high as{' '}
                        {maximumFormattedAmount} per night.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        Sitters on Rover are free to set their rates. The median cost to hire a
                        sitter for dog boarding in {neighborhoodName} on Rover as of{' '}
                        {yearMonthLastUpdated} was about {formattedAmount} per night, after
                        factoring in{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          Rover’s service fees
                        </Link>
                        . A sitter’s rate may also change as you customize your booking to fit your
                        and your dog’s needs.
                      </Trans>
                    }
                    other={
                      <Trans>
                        Sitters on Rover are free to set their rates. The median cost to hire a{' '}
                        {locationName} sitter for dog boarding in this area on Rover as of{' '}
                        {yearMonthLastUpdated} was about {formattedAmount} per night, after
                        factoring in{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          Rover’s service fees
                        </Link>
                        . A sitter’s rate may also change as you customize your booking to fit your
                        and your dog’s needs.
                      </Trans>
                    }
                  />
                </Paragraph>
              </FrequentlyAskedQuestion>
            );
          })()}

        {/* 3 */}
        {'howDoIContactAPetSitterOrDogWalkerRoverApp' in links &&
          'howDoIContactAPetSitterOrDogWalkerComputer' in links && (
            <FrequentlyAskedQuestion
              question={t`How do I contact a ${locationName} sitter for dog boarding?`}
            >
              <>
                <Paragraph webProps={{ maxWidth: 'none' }}>
                  <Trans>
                    If you’re searching for a {locationName} dog boarding sitter for the first time,
                    visit the sitter’s profile and select the Contact button.
                  </Trans>
                </Paragraph>
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    If you have an active request or have booked a service with a sitter before,
                    learn more about how to do this in the{' '}
                    <Link href={links.howDoIContactAPetSitterOrDogWalkerRoverApp} target="_blank">
                      Rover app
                    </Link>{' '}
                    or on your{' '}
                    <Link href={links.howDoIContactAPetSitterOrDogWalkerComputer} target="_blank">
                      computer
                    </Link>
                    .
                  </Trans>
                </Paragraph>
              </>
            </FrequentlyAskedQuestion>
          )}

        {/* 4 */}
        {maxSearchableSittersNum &&
          (() => {
            const { yearMonthLastUpdated, asNumber: sittersCount } = maxSearchableSittersNum;

            return (
              <FrequentlyAskedQuestion
                question={select(localityType, {
                  city: `How many sitters provide dog boarding in ${cityName}?`,
                  neighborhood: `How many sitters provide dog boarding in ${neighborhoodName}?`,
                  other: `How many ${locationName} sitters provide dog boarding?`,
                })}
              >
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        As of {yearMonthLastUpdated}, {sittersCount} sitters provided dog boarding
                        in {cityName}. You can filter, sort, expand your radius, read reviews, and
                        compare pricing to find the perfect sitter near you. As a reminder, dog
                        boarding sitters joining Rover must pass a background check for you and your
                        dog’s safety.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        As of {yearMonthLastUpdated}, {sittersCount} sitters provided dog boarding
                        in {neighborhoodName}. You can filter, sort, expand your radius, read
                        reviews, and compare pricing to find the perfect sitter near you. As a
                        reminder, dog boarding sitters joining Rover must pass a background check
                        for you and your dog’s safety.
                      </Trans>
                    }
                    other={
                      <Trans>
                        As of {yearMonthLastUpdated}, {sittersCount} {locationName} sitters provided
                        dog boarding. You can filter, sort, expand your radius, read reviews, and
                        compare pricing to find the perfect sitter near you. As a reminder, dog
                        boarding sitters joining Rover must pass a background check for you and your
                        dog’s safety.
                      </Trans>
                    }
                  />
                </Paragraph>
              </FrequentlyAskedQuestion>
            );
          })()}

        {/* 5 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `How long will it take for me to find a dog boarding sitter in ${cityName}?`,
            neighborhood: `How long will it take for me to find a dog boarding sitter in ${neighborhoodName}?`,
            other: `How long will it take for me to find a ${locationName} dog boarding sitter?`,
          })}
        >
          {sittersWithMinResponseTimeIn60minPct ? (
            (() => {
              const percent = sittersWithMinResponseTimeIn60minPct.asPercent;
              return (
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    Rover makes it easy to reach out to multiple sitters about your booking.
                    Typically, {percent} of {locationName} dog boarding sitters respond in under an
                    hour.
                  </Trans>
                </Paragraph>
              );
            })()
          ) : (
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              <Trans>
                Typically, dog boarding sitters on Rover respond in under an hour, so you should be
                matched with the perfect sitter for you and your dog in no time!
              </Trans>
            </Paragraph>
          )}
        </FrequentlyAskedQuestion>

        {/* 6 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `How experienced are sitters providing dog boarding in ${cityName}?`,
            neighborhood: `How experienced are sitters providing dog boarding in ${neighborhoodName}?`,
            other: `How experienced are ${locationName} sitters providing dog boarding?`,
          })}
        >
          <>
            {/* we have num years of experience and avg repeat customers */}
            {yearsOfExperienceNum &&
              avgRepeatCustomerAmongSittersWithRepeatNum &&
              (() => {
                const yearsOfExperience = yearsOfExperienceNum.raw;
                const repeatCustomersCount = avgRepeatCustomerAmongSittersWithRepeatNum.asNumber;
                return (
                  <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                    <Select
                      value={localityType}
                      _city={
                        <Trans>
                          Sitters who provide dog boarding in {cityName} have an average of{' '}
                          {yearsOfExperience} years of experience. Dog boarding sitters with repeat
                          customers have an average of {repeatCustomersCount} repeat clients.
                          Experience can vary from sitter to sitter, but you can view reviews, years
                          of experience, and number of repeat clients when you compare sitters in{' '}
                          {locationName}.
                        </Trans>
                      }
                      _neighborhood={
                        <Trans>
                          Sitters who provide dog boarding in {neighborhoodName} have an average of{' '}
                          {yearsOfExperience} years of experience. Dog boarding sitters with repeat
                          customers have an average of {repeatCustomersCount} repeat clients.
                          Experience can vary from sitter to sitter, but you can view reviews, years
                          of experience, and number of repeat clients when you compare sitters in{' '}
                          {locationName}.
                        </Trans>
                      }
                      other={
                        <Trans>
                          {locationName} sitters who provide dog boarding have an average of{' '}
                          {yearsOfExperience} years of experience. Dog boarding sitters with repeat
                          customers have an average of {repeatCustomersCount} repeat clients.
                          Experience can vary from sitter to sitter, but you can view reviews, years
                          of experience, and number of repeat clients when you compare sitters.
                        </Trans>
                      }
                    />
                  </Paragraph>
                );
              })()}

            {/* we have avg repeat customers, but not num years of experience */}
            {!yearsOfExperienceNum &&
              avgRepeatCustomerAmongSittersWithRepeatNum &&
              (() => {
                const repeatCustomersCount = avgRepeatCustomerAmongSittersWithRepeatNum.asNumber;
                return (
                  <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                    <Select
                      value={localityType}
                      _city={
                        <Trans>
                          Dog boarding sitters with repeat customers have an average of{' '}
                          {repeatCustomersCount} repeat clients. Experience can vary from sitter to
                          sitter, but you can view reviews, years of experience, and number of
                          repeat clients when you compare sitters in {cityName}.
                        </Trans>
                      }
                      _neighborhood={
                        <Trans>
                          Dog boarding sitters with repeat customers have an average of{' '}
                          {repeatCustomersCount} repeat clients. Experience can vary from sitter to
                          sitter, but you can view reviews, years of experience, and number of
                          repeat clients when you compare sitters in {neighborhoodName}.
                        </Trans>
                      }
                      other={
                        <Trans>
                          Dog boarding sitters with repeat customers have an average of{' '}
                          {repeatCustomersCount} repeat clients. Experience can vary from sitter to
                          sitter, but you can view reviews, years of experience, and number of
                          repeat clients when you compare sitters.
                        </Trans>
                      }
                    />
                  </Paragraph>
                );
              })()}

            {/* we have num years of experience, but not avg repeat customers */}
            {yearsOfExperienceNum &&
              !avgRepeatCustomerAmongSittersWithRepeatNum &&
              (() => {
                const yearsOfExperience = yearsOfExperienceNum.raw;

                return (
                  <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                    <Select
                      value={localityType}
                      _city={
                        <Trans>
                          Sitters providing dog boarding in {cityName} have an average of{' '}
                          {yearsOfExperience} years of experience. This can vary widely from sitter
                          to sitter, but you can view reviews, years of experience, and number of
                          repeat clients when comparing sitters available in {cityName}.
                        </Trans>
                      }
                      _neighborhood={
                        <Trans>
                          Sitters providing dog boarding in {neighborhoodName} have an average of{' '}
                          {yearsOfExperience} years of experience. This can vary widely from sitter
                          to sitter, but you can view reviews, years of experience, and number of
                          repeat clients when comparing sitters available in {neighborhoodName}.
                        </Trans>
                      }
                      other={
                        <Trans>
                          {locationName} sitters providing dog boarding have an average of{' '}
                          {yearsOfExperience} years of experience. This can vary widely from sitter
                          to sitter, but you can view reviews, years of experience, and number of
                          repeat clients when comparing sitters.
                        </Trans>
                      }
                    />
                  </Paragraph>
                );
              })()}

            {/* we don't have num years of experience or avg repeat customers */}
            {!yearsOfExperienceNum && !avgRepeatCustomerAmongSittersWithRepeatNum && (
              <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                <Select
                  value={localityType}
                  _city={
                    <Trans>
                      Experience can vary widely from sitter to sitter, but you can view reviews,
                      years of experience, and number of repeat clients when comparing sitters
                      available in {cityName}.
                    </Trans>
                  }
                  _neighborhood={
                    <Trans>
                      Experience can vary widely from sitter to sitter, but you can view reviews,
                      years of experience, and number of repeat clients when comparing sitters
                      available in {neighborhoodName}.
                    </Trans>
                  }
                  other={
                    <Trans>
                      Experience can vary widely from sitter to sitter, but you can view reviews,
                      years of experience, and number of repeat clients when comparing sitters.
                    </Trans>
                  }
                />
              </Paragraph>
            )}
          </>
        </FrequentlyAskedQuestion>

        {/* 7 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `What sort of dog boarding services do sitters in ${cityName} offer on Rover?`,
            neighborhood: `What sort of dog boarding services do sitters in ${neighborhoodName} offer on Rover?`,
            other: `What sort of dog boarding services do ${locationName} sitters offer on Rover?`,
          })}
        >
          <>
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              <Select
                value={localityType}
                _city={
                  <Trans>
                    Rover makes it easy to find {cityName} dog boarding sitters to provide loving
                    care from their own home. The background-checked, 5-star sitters you can find on
                    Rover will welcome your dog into their home when you’re away—whether it’s just
                    for the weekend or longer. Dog boarding is great for:
                  </Trans>
                }
                _neighborhood={
                  <Trans>
                    Rover makes it easy to find {neighborhoodName} dog boarding sitters to provide
                    loving care from their own home. The background-checked, 5-star sitters you can
                    find on Rover will welcome your dog into their home when you’re away—whether
                    it’s just for the weekend or longer. Dog boarding is great for:
                  </Trans>
                }
                other={
                  <Trans>
                    Rover makes it easy to find {locationName} dog boarding sitters to provide
                    loving care from their own home. The background-checked, 5-star sitters you can
                    find on Rover will welcome your dog into their home when you’re away—whether
                    it’s just for the weekend or longer. Dog boarding is great for:
                  </Trans>
                }
              />
            </Paragraph>
            <UnorderedList>
              <ListItem>
                <Trans comment="Part of a list describing who would benefit from a particular service type">
                  Dogs of all ages and abilities, including puppies
                </Trans>
              </ListItem>
              <ListItem>
                <Trans comment="Part of a list describing who would benefit from a particular service type">
                  Pet parents looking for a safe, loving alternative to a kennel
                </Trans>
              </ListItem>
              <ListItem>
                <Trans comment="Part of a list describing who would benefit from a particular service type">
                  Dogs who’d love to socialize with their sitter’s pets
                </Trans>
              </ListItem>
            </UnorderedList>
          </>
        </FrequentlyAskedQuestion>

        {/* 8 */}
        {maxSearchableSittersNum && (
          <FrequentlyAskedQuestion
            question={t`My dog has special needs. Can I find dog boarding on Rover that can accommodate my dog’s needs?`}
          >
            {sittersCanProvideSpecialCarePct &&
            sittersCanProvideDailyExercisePct &&
            sittersCanProvideMedicationPct
              ? (() => {
                  const sitterCount = maxSearchableSittersNum.asNumber;
                  const dailyExercisePercent = sittersCanProvideDailyExercisePct.asPercent;
                  const provideMedicationPercent = sittersCanProvideMedicationPct.asPercent;
                  const specialCarePercent = sittersCanProvideSpecialCarePct.asPercent;
                  return (
                    <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                      <Select
                        value={localityType}
                        _city={
                          <Trans>
                            There are {sitterCount} dog boarding sitters in {cityName} on Rover, so
                            you’re sure to find the perfect match for your dog! In {cityName},{' '}
                            {dailyExercisePercent} of sitters offer daily exercise,{' '}
                            {provideMedicationPercent} offer medication administration, and{' '}
                            {specialCarePercent} have experience with senior dogs and dogs with
                            special needs. We know each dog is unique and may have specific care
                            instructions. You can filter, sort, expand your radius, read reviews,
                            and compare pricing to find the perfect dog boarding sitter near you. As
                            a reminder, sitters joining Rover must pass a background check for you
                            and your dog’s safety.
                          </Trans>
                        }
                        _neighborhood={
                          <Trans>
                            There are {sitterCount} dog boarding sitters in {neighborhoodName} on
                            Rover, so you’re sure to find the perfect match for your dog! In{' '}
                            {neighborhoodName}, {dailyExercisePercent} of sitters offer daily
                            exercise, {provideMedicationPercent} offer medication administration,
                            and {specialCarePercent} have experience with senior dogs and dogs with
                            special needs. We know each dog is unique and may have specific care
                            instructions. You can filter, sort, expand your radius, read reviews,
                            and compare pricing to find the perfect dog boarding sitter near you. As
                            a reminder, sitters joining Rover must pass a background check for you
                            and your dog’s safety.
                          </Trans>
                        }
                        other={
                          <Trans>
                            There are {sitterCount} {locationName} dog boarding sitters on Rover, so
                            you’re sure to find the perfect match for your dog!{' '}
                            {dailyExercisePercent} of {locationName} sitters offer daily exercise,{' '}
                            {provideMedicationPercent} offer medication administration, and{' '}
                            {specialCarePercent} have experience with senior dogs and dogs with
                            special needs. We know each dog is unique and may have specific care
                            instructions. You can filter, sort, expand your radius, read reviews,
                            and compare pricing to find the perfect dog boarding sitter near you. As
                            a reminder, sitters joining Rover must pass a background check for you
                            and your dog’s safety.
                          </Trans>
                        }
                      />
                    </Paragraph>
                  );
                })()
              : (() => {
                  const sitterCount = maxSearchableSittersNum.asNumber;
                  return (
                    <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                      <Select
                        value={localityType}
                        _city={
                          <Trans>
                            There are {sitterCount} dog boarding sitters in {cityName} on Rover, so
                            you’re sure to find the perfect match for your dog! We understand that
                            each dog is unique and may require specific instructions. You can
                            filter, sort, expand your radius, read reviews, and compare pricing to
                            find the perfect dog boarding sitter near you. As a reminder, sitters
                            joining Rover must pass a background check for you and your dog’s
                            safety.
                          </Trans>
                        }
                        _neighborhood={
                          <Trans>
                            There are {sitterCount} dog boarding sitters in {neighborhoodName} on
                            Rover, so you’re sure to find the perfect match for your dog! We
                            understand that each dog is unique and may require specific
                            instructions. You can filter, sort, expand your radius, read reviews,
                            and compare pricing to find the perfect dog boarding sitter near you. As
                            a reminder, sitters joining Rover must pass a background check for you
                            and your dog’s safety.
                          </Trans>
                        }
                        other={
                          <Trans>
                            There are {sitterCount} {locationName} dog boarding sitters on Rover, so
                            you’re sure to find the perfect match for your dog! We understand that
                            each dog is unique and may require specific instructions. You can
                            filter, sort, expand your radius, read reviews, and compare pricing to
                            find the perfect dog boarding sitter near you. As a reminder, sitters
                            joining Rover must pass a background check for you and your dog’s
                            safety.
                          </Trans>
                        }
                      />
                    </Paragraph>
                  );
                })()}
          </FrequentlyAskedQuestion>
        )}

        {/* 9 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `Do sitters providing dog boarding in ${cityName} pass background checks?`,
            neighborhood: `Do sitters providing dog boarding in ${neighborhoodName} pass background checks?`,
            other: `Do ${locationName} sitters providing dog boarding pass background checks?`,
          })}
        >
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              Yes! Sitters joining Rover must pass a background check before offering their
              services. You can also easily keep in touch with your dog boarding sitter through
              messages on Rover for delightful photo updates. The Rover team is available 24/7, and
              your sitter has access to advice from qualified veterinary professionals. In the rare
              event of any issues during a booking, rest easy knowing your pet is covered by the
              Rover Guarantee reimbursement program for eligible veterinary care.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 10 */}
        {'howDoICancelABookingAndReceiveARefundRoverApp' in links &&
          'howDoICancelABookingAndReceiveARefundComputer' in links && (
            <FrequentlyAskedQuestion question="How do I cancel a dog boarding service and receive a refund?">
              <>
                <Paragraph webProps={{ maxWidth: 'none' }}>
                  <i>
                    <Trans>
                      <strong>Note:</strong> If your dog boarding sitter requested to cancel this
                      booking, then they need to cancel from their account. Please contact them via{' '}
                      <Link href="/account/inbox/" target="_blank">
                        Rover Messages
                      </Link>
                      .
                    </Trans>
                  </i>
                </Paragraph>
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    We get it—sometimes plans change and you may need to cancel your dog boarding
                    service. You can cancel an upcoming booking directly from the{' '}
                    <Link
                      href={links.howDoICancelABookingAndReceiveARefundRoverApp}
                      target="_blank"
                    >
                      Rover app
                    </Link>{' '}
                    or your{' '}
                    <Link
                      href={links.howDoICancelABookingAndReceiveARefundComputer}
                      target="_blank"
                    >
                      computer
                    </Link>
                    , in accordance with your sitter’s cancellation policy.
                  </Trans>
                </Paragraph>
              </>
            </FrequentlyAskedQuestion>
          )}
      </FirstFAQIsOpen>
    </FrequentlyAskedQuestions>
  );
};

export default DogBoardingFaqs;
